import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createDraftSafeSelector,
} from "@reduxjs/toolkit";
import { RootState } from "../index";
import getQueryString from "libs/getQueryString";
import { setLoadingDirect } from "store/reducers/global";
import { get } from "lodash";
import { resetUserSlice, getBot } from "store/reducers/user";
import {
  handleInputChange,
  setMessages,
  setChatMode,
  loginWelcomePage,
  resetChatSlice,
  mapInputValue,
  pushSelfMessage,
  mapMessages,
  pushBotMessage,
  filtedCompanySelector,
} from "store/reducers/chat";
import { ChatMode, MessageFrom, MessageType } from "types/chat";
import history from "libs/history";
import storage from "libs/storage";
import produce from "immer";
import { PHONEREG, EMAILREG } from "libs/regex";
import { message as AntdMessage, Modal } from "antd";
// import isLocalhost from "libs/isLocalhost";
import LoginService from "services/login";
import { StatusCode } from "libs/http/constant";

export function isLogin() {
  const token = storage.getToken();
  const isValidToken =
    token &&
    token !== "undefined" &&
    token !== "null" &&
    token !== "false" &&
    token !== "0" &&
    token !== "isNaN";
  if (!isValidToken) {
    storage.removeToken();
  }
  return !!isValidToken;
}

export const goLogout = createAsyncThunk(
  "login/goLogout",
  ({ next }: { next?: string }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;

    const isInLoginPage = window.location.href.indexOf("/login") > 0;
    if (isInLoginPage) {
      return;
    }

    dispatch(resetUserSlice());
    dispatch(resetChatSlice());
    dispatch(resetLoginSlice());
    dispatch(cleanCountdown({}));

    if (next) {
      return (window.location.href = next);
    }

    const urlCompanyID = getQueryString("companyID");
    const preCompanyID = urlCompanyID || `${storage.getCompanyID()}`;
    const filtedCompanyID = preCompanyID.match(/\d+/g);
    const companyID = filtedCompanyID
      ? `?companyID=${filtedCompanyID}&forbitLogin=true`
      : "";
    // const prefix = isLocalhost() ? "/#/login" : "/chatbot-pc/#/login";
    // window.location.href = `${window.location.origin}${prefix}${companyID}`;

    storage.removeToken();
    storage.setCompanyID("");
    history.push(`/login${companyID}`);
  }
);

(window as any).goLogout = goLogout;

export const goLogin = createAsyncThunk(
  "login/goLogin",
  async (arg: any, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const dispatch = thunkAPI.dispatch;
    const companyID = mapCompanyID(state);
    const accountType = mapAccountType(state);
    const account = mapAccount(state);
    const passwordType = mapPasswordType(state);
    const password = mapPassword(state);

    if (companyID && accountType && passwordType && account && password) {
      const res = await LoginService.login({
        company_id: companyID,
        account_type: accountType,
        auth_type: passwordType,
        account,
        auth_code: password,
      });
      const success = get(res, "code") === StatusCode.success;
      const token = get(res, "result.token");
      const greeting = get(res, "result.button_info.greeting");
      const btn = get(res, "result.button_info.button");
      const message = get(res, "message", "登录成功");

      if (success && token) {
        storage.setToken(token);
        storage.setCompanyID(`${companyID}`);

        const modal = Modal.success({
          title: message,
          okText: "确定",
          onOk() {
            history.push("/welcome");
          },
          centered: true,
        });

        setTimeout(() => {
          dispatch(setMessages([]));
          dispatch(cleanCountdown({}));
          dispatch(resetLoginSlice());
          modal && modal.destroy();
          history.push("/welcome");

          if (greeting && btn) {
            Modal.info({
              title: greeting,
              okText: btn,
              centered: true,
            });
          }
        }, 1000);
      } else {
        dispatch(pushBotMessage({ payload: message }));
      }
    }
  }
);

export enum AccountType {
  phone = 1,
  email = 2,
  employee_number = 3,
}

export enum PasswordType {
  dynamic = 1,
  static = 2,
}

export const avaliableAccountTypes = [
  AccountType.phone,
  AccountType.email,
  AccountType.employee_number,
];

export const avaliablePasswordTypes = [
  PasswordType.dynamic,
  PasswordType.static,
];

export interface CompanyInfo {
  abbr: string;
  company_id: number;
  company_name: string;
  has_password: boolean;
  need_verify: boolean;
  user_id: number;
  user_type: number;
}

export const defaultCountdown = 60;

interface IState {
  signAgreement: boolean; // 隐私协议

  companyID?: number; // 公司ID
  accountType?: AccountType; // 账号类型
  account?: string; // 账号
  passwordType?: PasswordType; // 密码类型
  password?: string; // 密码

  countdown: number; // 倒计时数字
  isCounting: boolean; // 计时中
  countdownVisible: boolean;

  selectedCompany?: CompanyInfo; // 已选的公司信息
  checkedAccount: boolean; // 验证账号之后不可返回之前的步骤
  switchedPasswordType: boolean; // 只能切换一次密码类型
}

const initialState: IState = {
  signAgreement: false,
  companyID: undefined,
  accountType: undefined,
  account: "",
  passwordType: undefined,
  password: "",

  countdown: defaultCountdown,
  isCounting: false,
  countdownVisible: false,

  selectedCompany: undefined,
  checkedAccount: false,
  switchedPasswordType: false,
};

export let countdownTimer: any;
// export let countdownDoing = false;
const countdownFN = (setter: any) => {
  const fn = (last: number, resolve: any) => {
    if (last <= 0) {
      resolve({});
      return;
    }
    clearTimeout(countdownTimer);
    countdownTimer = null;
    countdownTimer = setTimeout(() => {
      const next = last - 1;
      if (next >= 0) {
        setter(next);
        fn(next, resolve);
      } else {
        resolve({});
      }
    }, 1000);
  };
  return new Promise((resolve) => {
    fn(defaultCountdown, resolve);
  });
};

export const startCountdown = createAsyncThunk(
  "login/startCountdown",
  async (arg: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;
    const isCounting = mapIsCounting(state);
    if (isCounting) return;
    // console.log("countdown start");
    dispatch(setIsCounting(true));
    dispatch(setCountdown(defaultCountdown));
    await countdownFN((n: any) => dispatch(setCountdown(n)));
    dispatch(setIsCounting(false));
    // console.log("countdown finished");
  }
);

export const cleanCountdown = createAsyncThunk(
  "login/cleanCountdown",
  (arg: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    clearTimeout(countdownTimer);
    countdownTimer = null;
    dispatch(setCountdown(defaultCountdown));
    dispatch(setIsCounting(false));
  }
);

const loginReset = async (dispatch: any) => {
  dispatch(resetUserSlice());
  dispatch(resetChatSlice());
  dispatch(resetLoginSlice());
  dispatch(setChatMode(ChatMode.LOGIN));
  dispatch(setMessages(loginWelcomePage));
  await dispatch(getBot({}));
};

export const initLogin = createAsyncThunk(
  "login/initLogin",
  async (arg: any, thunkAPI) => {
    const _crop_id = getQueryString("crop_id");
    const _code = getQueryString("code");
    if (_crop_id || _code) {
      await loginReset(thunkAPI.dispatch);
      return;
    }

    const preCompanyID = storage.getCompanyID();
    const urlCompanyID = getQueryString("companyID");
    const logined = isLogin();
    if (
      logined &&
      preCompanyID &&
      urlCompanyID &&
      preCompanyID !== urlCompanyID
    ) {
      storage.removeToken();
      storage.setCompanyID("");
      window.location.reload();
      return;
    } else if (logined) {
      return history.replace("/chat");
    }

    setLoadingDirect(true);
    const dispatch = thunkAPI.dispatch;

    // reset
    await loginReset(dispatch);
    // dispatch(resetUserSlice());
    // dispatch(resetChatSlice());
    // dispatch(resetLoginSlice());
    // dispatch(setChatMode(ChatMode.LOGIN));
    // dispatch(setMessages(loginWelcomePage));
    // await dispatch(getBot({}));

    // get default company ID
    const strCompanyID = getQueryString("companyID");
    const companyID = parseInt(strCompanyID, 10);
    Number.isSafeInteger(companyID) &&
      companyID &&
      dispatch(setCompanyID(companyID));

    // get default account type
    const strAccountType = getQueryString("accountType");
    const numAccountType = parseInt(strAccountType, 10);
    const isValidAccountType =
      !Number.isNaN(numAccountType) &&
      avaliableAccountTypes.includes(numAccountType);

    if (isValidAccountType) {
      if (
        (numAccountType === AccountType.employee_number &&
          Number.isSafeInteger(companyID)) ||
        numAccountType === AccountType.phone ||
        numAccountType === AccountType.email
      ) {
        dispatch(setAccountType(numAccountType));
      }
    }

    setLoadingDirect(false);
  }
);

export const handleSignAgreement = createAsyncThunk(
  "login/handleSignAgreement",
  async (arg: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;
    const signAgreement = mapSignAgreement(state);
    const accountType = mapAccountType(state);
    if (!signAgreement) {
      dispatch(setSignAgreement(true));

      if (accountType) {
        dispatch(handleSetAccountType({ type: accountType }));
      } else {
        dispatch(
          pushBotMessage({
            payload: "请问你是想使用手机号、邮箱还是工号进行登录呢？",
          })
        );
      }
    }
  }
);

export const handleSetAccountType = createAsyncThunk(
  "login/handleSetAccountType",
  async ({ type }: { type: AccountType }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    dispatch(setAccountType(type));

    const text =
      type === AccountType.email
        ? "请输入邮箱"
        : type === AccountType.phone
        ? "请输入手机号"
        : type === AccountType.employee_number
        ? "请输入工号"
        : "";
    text && dispatch(pushBotMessage({ payload: text }));
  }
);

export const handleForgetPassword = createAsyncThunk(
  "login/handleForgetPassword",
  async (any: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;
    const accountType = mapAccountType(state);
    const selectedCompany = mapSelectedCompany(state);
    const hasPhone = get(selectedCompany, "has_phone");
    const hasEmail = get(selectedCompany, "has_email");
    const canReceiveAuthCode = hasPhone || hasEmail;
    if (accountType === AccountType.employee_number) {
      dispatch(
        pushBotMessage({
          payload: canReceiveAuthCode
            ? "请重新登录后选择手机或邮箱验证码登录"
            : "请与后台管理员联系",
        })
      );
    }
  }
);

export const validPhone = (v: string) => PHONEREG.test(v);
export const validEmail = (v: string) => EMAILREG.test(v);

export const handleInputOnChange = createAsyncThunk(
  "login/handleInputOnChange",
  ({ val }: { val: string }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;

    const accountType = mapAccountType(state);
    const account = mapAccount(state);
    const passwordType = mapPasswordType(state);
    // const val = mapInputValue(state);
    let filtedVal = val.replace(" ", "");

    // console.log('filtedVal: ', filtedVal);
    if (accountType && !passwordType) {
      if (accountType === AccountType.phone) {
        filtedVal = filtedVal.slice(0, 11);
      } else if (
        accountType === AccountType.email ||
        accountType === AccountType.employee_number
      ) {
        filtedVal = filtedVal.slice(0, 40);
      }
    } else if (accountType && account && passwordType) {
      if (passwordType === PasswordType.dynamic) {
        filtedVal = val.slice(0, 6);
      } else {
        filtedVal = val.slice(0, 40);
      }
    }

    dispatch(handleInputChange(filtedVal));
  }
);

export const handleInputConfirm = createAsyncThunk(
  "login/handleInputConfirm",
  (arg: any, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const accountType = mapAccountType(state);
    if (!accountType) {
      return;
    }
    const account = mapAccount(state);
    const dispatch = thunkAPI.dispatch;
    const passwordType = mapPasswordType(state);
    // const password = mapPassword(state);
    const val = mapInputValue(state);

    if (accountType && !passwordType) {
      if (accountType === AccountType.phone && !validPhone(val)) {
        return AntdMessage.warning(`手机号格式错误`);
      } else if (accountType === AccountType.email && !validEmail(val)) {
        return AntdMessage.warning(`邮箱格式错误`);
      } else if (
        accountType === AccountType.employee_number &&
        val.length > 40
      ) {
        return AntdMessage.warning(`工号格式错误`);
      }

      dispatch(setAccount(val));
      dispatch(sendAccountMessage({ val }));

      dispatch(checkAccount({}));
    } else if (accountType && account && passwordType) {
      if (passwordType === PasswordType.dynamic && val.length !== 6) {
        return AntdMessage.warning(`验证码格式错误`);
      } else if (
        passwordType === PasswordType.static &&
        (val.length < 6 || val.length > 40)
      ) {
        return AntdMessage.warning(`密码格式错误`);
      } else {
        dispatch(setPassword(val));
        const authCodeMsg =
          passwordType === PasswordType.dynamic ? val : val.replace(/./g, "*");
        dispatch(pushSelfMessage({ payload: authCodeMsg }));
        dispatch(handleInputChange(""));
        dispatch(goLogin({}));
      }
    }
  }
);

export const sendAccountMessage = createAsyncThunk(
  "login/sendAccountMessage",
  ({ val }: { val: string }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    dispatch(
      pushSelfMessage({
        payload: {
          data: {
            content: val,
          },
          type: MessageType.client_phone_msg,
        },
      })
    );
    dispatch(handleInputChange(""));
  }
);

export const cleanAccountMessage = createAsyncThunk(
  "login/cleanAccountMessage",
  (arg: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;

    // exist account message filter
    const preMessages = mapMessages(state);
    const nextMessages = produce(preMessages, (draftState) => {
      const accountMessageIndex = draftState.findIndex((v) => {
        return (
          get(v, "from") === MessageFrom.SELF &&
          get(v, "messages").some(
            (vv) => get(vv, "type") === MessageType.client_phone_msg
          )
        );
      });

      if (accountMessageIndex > -1) {
        draftState.splice(accountMessageIndex, 1);
      }
    });
    dispatch(setMessages(nextMessages));
  }
);

export const resetAccountType = createAsyncThunk(
  "login/resetAccountType",
  (arg: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;

    // dispatch(cleanAccountMessage({}));
    dispatch(setAccountType(undefined));
    dispatch(setAccount(""));
    dispatch(handleInputChange(""));
  }
);

export const resetAccount = createAsyncThunk(
  "login/resetAccount",
  (arg: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;
    const account = mapAccount(state) || "";

    // dispatch(cleanAccountMessage({}));
    dispatch(setAccount(""));
    dispatch(handleInputChange(account));
  }
);

export const showCompanyPickerMessage = createAsyncThunk(
  "login/showCompanyPickerMessage",
  async ({ company_infos }: { company_infos: any[] }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    dispatch(
      pushBotMessage({
        payload: [
          "请选择要登录的公司",
          {
            type: MessageType.client_chose_companies,
            data: company_infos,
          },
        ],
      })
    );
  }
);

let checkAccountRunning = false;

export const checkAccount = createAsyncThunk(
  "login/checkAccount",
  async (arg: any, thunkAPI) => {
    if (checkAccountRunning) return;
    checkAccountRunning = true;

    setLoadingDirect(true);
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;

    const companyID = mapCompanyID(state);
    const accountType = mapAccountType(state);
    const account = mapAccount(state);
    const checkedAccount = mapCheckedAccount(state);
    if (account && accountType && !checkedAccount) {
      const res = await LoginService.checkAccount({
        account,
        account_type: accountType,
        company_id: companyID,
      });
      const success = get(res, "code") === StatusCode.success;
      const message = get(res, "message", "验证失败，请重试");
      const company_infos = get(res, "result.account_responses", []);
      const isSafeAccount = company_infos.length > 0;
      const selectedCompany = company_infos.find(
        (v: CompanyInfo) => get(v, "company_id") === companyID
      );

      if (success && isSafeAccount && !companyID) {
        if (company_infos.length === 1) {
          const defaultCompany = get(company_infos, "[0]");
          dispatch(chooseCompany({ company: defaultCompany }));
        } else {
          dispatch(showCompanyPickerMessage({ company_infos }));
        }
        dispatch(setCheckedAccount(true));
      } else if (success && isSafeAccount && companyID && selectedCompany) {
        dispatch(chooseCompany({ company: selectedCompany }));
        dispatch(setCheckedAccount(true));
      } else if (success && isSafeAccount && companyID && !selectedCompany) {
        dispatch(pushBotMessage({ payload: "没有找到链接公司信息" }));
        dispatch(setCheckedAccount(false));
        dispatch(setAccount(""));
      } else {
        dispatch(pushBotMessage({ payload: message }));
        dispatch(setCheckedAccount(false));
        dispatch(setAccount(""));
      }
    }
    setLoadingDirect(false);
    checkAccountRunning = false;
  }
);

export const switchPasswordType = createAsyncThunk(
  "login/switchPasswordType",
  async (arg: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;
    const switched = mapSwitchedPasswordType(state);
    if (switched) return;
    const selectedCompany = mapSelectedCompany(state);
    const has_password = get(selectedCompany, "has_password");
    const passwordType = mapPasswordType(state);

    if (passwordType === PasswordType.static) {
      dispatch(setSwitchedPasswordType(true));
      dispatch(setPasswordType(PasswordType.dynamic));
      dispatch(sendSMS({}));
      // dispatch(pushBotMessage({ payload: "请输入验证码" }));
    } else if (passwordType === PasswordType.dynamic && has_password) {
      dispatch(setSwitchedPasswordType(true));
      dispatch(setPasswordType(PasswordType.static));
      dispatch(cleanCountdown({}));
      dispatch(pushBotMessage({ payload: "请输入密码" }));
    }
  }
);

export const sendSMS = createAsyncThunk(
  "login/sendSMS",
  async (arg: any, thunkAPI) => {
    setLoadingDirect(true);
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;
    const companyID = mapCompanyID(state);
    const account = mapAccount(state);
    const accountType = mapAccountType(state);
    if (companyID && accountType && account) {
      dispatch(setPasswordType(PasswordType.dynamic));
      const res = await LoginService.sendSMS({
        company_id: companyID,
        account,
        account_type: accountType,
      });
      const success = get(res, "code") === StatusCode.success;
      const message = get(res, "message", "发送失败请重试");
      if (success) {
        dispatch(setCountdownVisible(true));
        dispatch(startCountdown({}));
        dispatch(pushBotMessage({ payload: "请输入验证码" }));
      } else {
        dispatch(setCountdownVisible(true));
        dispatch(pushBotMessage({ payload: message }));
      }
    }
    setLoadingDirect(false);
  }
);

export const sendSMSArgVersion = createAsyncThunk(
  "login/sendSMSArgVersion",
  async (
    {
      companyID,
      accountType,
      account,
    }: { companyID?: number; account: string; accountType: AccountType },
    thunkAPI
  ) => {
    const dispatch = thunkAPI.dispatch;
    if (companyID && accountType && account) {
      setLoadingDirect(true);
      const res = await LoginService.sendSMS({
        company_id: companyID,
        account,
        account_type: accountType,
      });
      const success = get(res, "code") === StatusCode.success;
      const message = get(res, "message", "发送失败请重试");
      if (success) {
        dispatch(startCountdown({}));
      } else {
        AntdMessage.error(message);
        setLoadingDirect(false);
        throw Error(message);
      }
      setLoadingDirect(false);
    }
  }
);

export const initPasswordType = createAsyncThunk(
  "login/initPasswordType",
  async (arg: any, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const state = thunkAPI.getState() as RootState;
    const accountType = mapAccountType(state);
    const isEmployeeNumberAccount = accountType === AccountType.employee_number;
    const matchCompany = mapSelectedCompany(state);
    const has_password = get(matchCompany, "has_password");

    if (isEmployeeNumberAccount && !has_password) {
      dispatch(
        pushBotMessage({
          payload:
            "抱歉，后台没有预留您的密码，请与后台管理员联系，或点击重新登陆选择其他登录方式。",
        })
      );
      return;
    }

    if (matchCompany) {
      if (has_password) {
        dispatch(setPasswordType(PasswordType.static));
        dispatch(pushBotMessage({ payload: "请输入密码" }));
      } else {
        dispatch(setPasswordType(PasswordType.dynamic));
        // dispatch(pushBotMessage({ payload: "请输入验证码" }));
        await dispatch(sendSMS({}));
      }
    }
  }
);

export const chooseCompany = createAsyncThunk(
  "login/chooseCompany",
  async ({ company }: { company: CompanyInfo }, thunkAPI) => {
    const dispatch = thunkAPI.dispatch;
    const companyID = get(company, "company_id");

    if (Number.isSafeInteger(companyID)) {
      const hide = AntdMessage.loading("账号检查中...", 0);
      dispatch(setSelectedCompany(company));
      dispatch(setCompanyID(companyID));
      await dispatch(initPasswordType({}));
      dispatch(filtedCompanySelector());
      hide();
    }
  }
);

const login = createSlice({
  name: "login",
  initialState,
  reducers: {
    setCountdown(state, action: PayloadAction<number>) {
      state.countdown = action.payload;
    },
    setIsCounting(state, action: PayloadAction<boolean>) {
      state.isCounting = action.payload;
    },
    setCountdownVisible(state, action: PayloadAction<boolean>) {
      state.countdownVisible = action.payload;
    },
    setCompanyID(state, action: PayloadAction<number>) {
      state.companyID = action.payload;
    },
    setSignAgreement(state, action: PayloadAction<boolean>) {
      state.signAgreement = action.payload;
    },
    setAccountType(state, action: PayloadAction<AccountType | undefined>) {
      state.accountType = action.payload;
    },
    setAccount(state, action: PayloadAction<string>) {
      state.account = action.payload;
    },
    setPasswordType(state, action: PayloadAction<PasswordType>) {
      state.passwordType = action.payload;
    },
    setPassword(state, action: PayloadAction<string>) {
      state.password = action.payload;
    },
    setCheckedAccount(state, action: PayloadAction<boolean>) {
      state.checkedAccount = action.payload;
    },
    setSwitchedPasswordType(state, action: PayloadAction<boolean>) {
      state.switchedPasswordType = action.payload;
    },
    setSelectedCompany(state, action: PayloadAction<CompanyInfo>) {
      state.selectedCompany = action.payload;
    },
    resetLoginSlice(state) {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setCountdown,
  setIsCounting,
  setCountdownVisible,
  setCompanyID,
  setSignAgreement,
  setAccountType,
  setAccount,
  setPasswordType,
  setPassword,
  setCheckedAccount,
  setSwitchedPasswordType,
  setSelectedCompany,
  resetLoginSlice,
} = login.actions;

export const mapCountdown = (state: RootState) => state.login.countdown;
export const mapIsCounting = (state: RootState) => state.login.isCounting;
export const mapCountdownVisible = (state: RootState) =>
  state.login.countdownVisible;

export const mapSignAgreement = (state: RootState) => state.login.signAgreement;
export const mapCompanyID = (state: RootState) => state.login.companyID;
export const mapAccountType = (state: RootState) => state.login.accountType;
export const mapAccount = (state: RootState) => state.login.account;
export const mapPasswordType = (state: RootState) => state.login.passwordType;
export const mapPassword = (state: RootState) => state.login.password;
export const mapSelectedCompany = (state: RootState) =>
  state.login.selectedCompany;
export const mapCheckedAccount = (state: RootState) =>
  state.login.checkedAccount;
export const mapSwitchedPasswordType = (state: RootState) =>
  state.login.switchedPasswordType;

export const mapLoginReducer = (state: RootState) => state.login;
export const mapUserReducer = (state: RootState) => state.user;
export const mapChatReducer = (state: RootState) => state.chat;

export const mapSwitchPasswordTypeBtnVisiable = createDraftSafeSelector(
  mapLoginReducer,
  (login) => {
    const has_password = get(login.selectedCompany, "has_password");
    const isEmployeeNumberAccount =
      login.accountType === AccountType.employee_number;
    if (login.switchedPasswordType || isEmployeeNumberAccount) {
      return false;
    } else {
      return (
        (login.passwordType === PasswordType.dynamic && has_password) ||
        login.passwordType === PasswordType.static
      );
    }
  }
);

export const mapInputPlaceholder = createDraftSafeSelector(
  mapLoginReducer,
  (login) => {
    if (
      login.signAgreement &&
      !login.account &&
      login.accountType === AccountType.phone
    ) {
      return "请输入手机号";
    } else if (
      login.signAgreement &&
      !login.account &&
      login.accountType === AccountType.email
    ) {
      return "请输入邮箱";
    } else if (
      login.signAgreement &&
      !login.account &&
      login.accountType === AccountType.employee_number
    ) {
      return "请输入工号";
    } else if (login.accountType && login.account && login.passwordType) {
      return login.passwordType === PasswordType.dynamic
        ? "请输入验证码"
        : "请输入密码";
    } else {
      return "";
    }
  }
);

export const mapInputDisabled = createDraftSafeSelector(
  mapLoginReducer,
  (login) => {
    const tag =
      !login.signAgreement ||
      !login.accountType ||
      (login.account && login.accountType && !login.checkedAccount) ||
      (login.account &&
        login.accountType &&
        login.checkedAccount &&
        !login.companyID);
    return !!tag;
  }
);

export const mapInputType = createDraftSafeSelector(
  mapLoginReducer,
  (login) => {
    return "text";
  }
);

export const mapInputMaxLength = createDraftSafeSelector(
  mapLoginReducer,
  (login) => {
    if (login.passwordType === PasswordType.dynamic) {
      return 6;
    } else {
      return 40;
    }
  }
);

export default login.reducer;
